import {
  CONTEXT_TOKEN_MAX, envelope, FRAME_TO_HOST_TYPES, looksLikeJwt, prepareClaimedContext,
  prepareTrackEvent, readEnvelope,
  type FrameToHost, type HostToFrame, type IdentitySource,
} from '../protocol';

/**
 * A Navigation API, quando o navegador a tem.
 *
 * Declarada aqui porque o alvo de compilação do carregador é antigo de propósito — ele roda em qualquer
 * site — e a tipagem padrão não a conhece. Ver `watchNavigation`: é o que permite perceber navegação de
 * SPA sem tocar no `history` da página de outra pessoa.
 */
type WindowWithNavigation = Window & {
  navigation?: { addEventListener: (t: string, h: () => void) => void; removeEventListener: (t: string, h: () => void) => void };
};

type WindowWithIdle = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};
import { LAUNCHER_CSS, LAUNCHER_ICON_CLOSE, LAUNCHER_ICON_OPEN } from './styles';

/**
 * O carregador do Web Chat na página do cliente.
 *
 * API por comando, porque é a que funciona com a fila de antes do carregamento:
 *
 * ```js
 * NuklieChat('init', { publicKey: 'wc_pk_…' });
 * NuklieChat('open');
 * NuklieChat('on', 'message_received', fn);
 * ```
 *
 * ## O que mora aqui, e o que não
 *
 * Aqui: o botão, o selo de não lidas e a caixa do iframe, dentro de um Shadow DOM fechado — o CSS do
 * site não alcança nada disso. No iframe (origem Nuklie): sessão, token, conversa, socket. Esta página
 * nunca vê o token do visitante nem o texto das mensagens.
 *
 * ## Identidade
 *
 * `identityToken` (um JWT que o BACKEND do hospedeiro assinou) e `getIdentityToken` (uma função que busca
 * um novo nesse backend) são as únicas formas de dizer quem é o usuário. O token fica só na memória deste
 * objeto até o iframe estar pronto, vai por `identity.set` e é esquecido — nada em `localStorage`, cookie
 * ou atributo. `email`, `name`, `userId` e afins continuam recusados: texto solto não prova nada.
 *
 * ## Contexto
 *
 * `setContext` (declarado pela página), `clearContext` e `setVerifiedContext` (token assinado pelo backend)
 * só descem para o iframe. Aqui só a forma é conferida; o conteúdo quem julga é o backend. Nada de contexto
 * volta para a página, e não existe `getContext`. A página chama `setContext` quando a tela muda — o
 * carregador não observa a navegação do site.
 */

export type WebchatEventName =
  | 'ready' | 'open' | 'close' | 'conversation_started' | 'message_received'
  | 'conversation_closed' | 'unread_count_changed' | 'identity_changed' | 'error'
  /*
   * O suporte proativo, para a página saber o que o widget fez sozinho.
   *
   * Só o id público da regra: nunca o texto, o gatilho ou a condição que a fez disparar. A página já
   * sabe o que está mostrando; o que ela não precisa é da configuração do chat de quem a instalou.
   */
  | 'proactive_shown' | 'proactive_clicked' | 'proactive_dismissed';

type Callback = (payload: Record<string, unknown>) => void;

export type IdentityCallback = () => string | Promise<string>;

export interface InitOptions {
  publicKey: string;
  locale?: string;
}

/** Identidade em texto solto: recusada sempre. A identidade só entra assinada (`identityToken`). */
const IDENTITY_KEYS = ['email', 'name', 'userId', 'user_id', 'externalUserId', 'external_user_id', 'identity_token', 'user', 'context', 'phone'];
const ALLOWED_KEYS = ['publicKey', 'locale', 'identityToken', 'getIdentityToken', 'getContextToken'];
const EVENTS: WebchatEventName[] = [
  'ready', 'open', 'close', 'conversation_started', 'message_received', 'conversation_closed',
  'unread_count_changed', 'identity_changed', 'error',
  'proactive_shown', 'proactive_clicked', 'proactive_dismissed',
];

/** Quanto o carregador espera o `getIdentityToken` do hospedeiro antes de desistir. */
export const IDENTITY_CALLBACK_TIMEOUT_MS = 15_000;

const PUBLIC_KEY = /^wc_pk_[a-z0-9]{32}$/;

/** Acima de cabeçalhos fixos e modais comuns, e o valor de referência dos widgets de chat. */
export const Z_INDEX = 2147483000;

interface LauncherConfig {
  public_key: string;
  primary_color: string | null;
  position: 'bottom_right' | 'bottom_left';
  /** Este chat tem regra proativa ativa? É o que decide preparar o iframe antes de alguém clicar. */
  proactive?: boolean;
}

declare global {
  interface Window {
    __nuklieChatLauncher?: (config: LauncherConfig) => void;
  }
}

export class NuklieChatLoader {
  private options: InitOptions | null = null;
  private host: HTMLElement | null = null;
  private root: ShadowRoot | null = null;
  private launcher: HTMLButtonElement | null = null;
  private badge: HTMLElement | null = null;
  private panel: HTMLElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private frameReady = false;
  /** A aparência chegou e o botão está visível. Antes disso, `open` espera. */
  private launcherReady = false;
  private isOpen = false;
  private pendingOpen = false;
  private resetPending = false;
  /** O JWT esperando o iframe. Só memória; sai no `identity.set` e some. */
  private pendingIdentity: string | null = null;
  private identityCallback: IdentityCallback | null = null;
  private identityInFlight: Promise<string> | null = null;
  /** O último contexto declarado ainda não entregue ao iframe (só memória). */
  private pendingClaimedContext: Record<string, unknown> | null = null;
  /** O último token de contexto ainda não entregue ao iframe (só memória). */
  private pendingContextToken: string | null = null;
  private contextCallback: IdentityCallback | null = null;
  private contextInFlight: Promise<string> | null = null;
  private unread = 0;
  private listeners = new Map<WebchatEventName, Set<Callback>>();
  private configScript: HTMLScriptElement | null = null;
  /**
   * O balão proativo na tela.
   *
   * O carregador guarda o elemento, e não a regra: quem sabe qual regra está mostrando é o iframe, que
   * é quem a escolheu e quem conta a impressão. Duplicar o id aqui criaria uma segunda verdade sobre
   * "qual balão está no ar" — e a do carregador seria a errada no instante em que a outra mudasse.
   */
  private teaser: HTMLElement | null = null;
  /** `track` chamado antes de o iframe responder: guardado e entregue depois. */
  private pendingEvents: { event: string; payload: Record<string, unknown> | null }[] = [];
  private preloadTimer: ReturnType<typeof setTimeout> | null = null;
  private idleHandle: number | null = null;
  private lastPath: string | null = null;
  /** O iframe nasceu para avaliar regras, e não porque alguém pediu o chat. */
  private preloadedForProactive = false;
  private readonly onNavigation = () => this.reportRoute();
  private readonly onMessage = (event: MessageEvent) => this.handleFrameMessage(event);
  private readonly onResize = () => this.post('host.viewport', { mobile: this.isMobile() });
  private readonly onVisibility = () => this.post('host.visibility', { visible: this.doc.visibilityState === 'visible' });
  private readonly onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  constructor(
    private readonly win: Window,
    /** A origem de onde o `chat.js` foi servido — é a do iframe e a da API do widget. */
    private readonly frameOrigin: string,
  ) {}

  private get doc(): Document {
    return this.win.document;
  }

  command(nome: unknown, ...args: unknown[]): void {
    switch (nome) {
      case 'init': this.init(args[0]); break;
      case 'open': this.open(); break;
      case 'close': this.close(); break;
      case 'toggle':
        if (this.isOpen) this.close();
        else this.open();
        break;
      case 'reset': this.reset(); break;
      case 'identify': this.identify(args[0]); break;
      case 'setContext': this.setContext(args[0]); break;
      case 'clearContext': this.clearContext(); break;
      case 'setVerifiedContext': this.setVerifiedContext(args[0]); break;
      case 'track': this.track(args[0], args[1]); break;
      case 'destroy': this.destroy(); break;
      case 'on': this.on(args[0] as WebchatEventName, args[1] as Callback); break;
      case 'off': this.off(args[0] as WebchatEventName, args[1] as Callback); break;
      default:
        this.configError(`Comando desconhecido: ${String(nome)}.`);
    }
  }

  init(raw: unknown): void {
    if (!raw || typeof raw !== 'object') {
      this.configError('init precisa de { publicKey }.');

      return;
    }

    const opcoes = raw as Record<string, unknown>;
    const identidade = Object.keys(opcoes).filter(k => IDENTITY_KEYS.includes(k));

    if (identidade.length > 0) {
      // Recusar em voz alta é melhor que ignorar: quem passou `email` acredita que identificou o
      // visitante, e não identificou.
      this.configError(`init não aceita dados de identidade em texto (${identidade.join(', ')}). Gere um identityToken no seu backend.`);

      return;
    }

    const desconhecidas = Object.keys(opcoes).filter(k => !ALLOWED_KEYS.includes(k));
    if (desconhecidas.length > 0) console.warn(`[NuklieChat] Opções ignoradas: ${desconhecidas.join(', ')}.`);

    const publicKey = typeof opcoes.publicKey === 'string' ? opcoes.publicKey.trim() : '';

    if (!PUBLIC_KEY.test(publicKey)) {
      this.configError('publicKey inválida. Copie o código de instalação em Integrações › Web Chat.');

      return;
    }

    if (opcoes.identityToken !== undefined && !looksLikeJwt(opcoes.identityToken)) {
      this.configError('identityToken precisa ser o JWT gerado pelo seu backend.');

      return;
    }

    if (opcoes.getContextToken !== undefined && typeof opcoes.getContextToken !== 'function') {
      this.configError('getContextToken precisa ser uma função que devolve o token de contexto gerado pelo seu backend.');

      return;
    }

    if (opcoes.getIdentityToken !== undefined && typeof opcoes.getIdentityToken !== 'function') {
      this.configError('getIdentityToken precisa ser uma função que devolve o JWT gerado pelo seu backend.');

      return;
    }

    if (this.options) {
      if (this.options.publicKey === publicKey) return;

      // Uma instância por página. Trocar de conexão em silêncio misturaria duas conversas.
      this.configError('Já existe um Web Chat iniciado nesta página com outra chave. Chame destroy antes de iniciar outro.');

      return;
    }

    this.options = { publicKey, locale: typeof opcoes.locale === 'string' ? opcoes.locale : undefined };
    this.pendingIdentity = typeof opcoes.identityToken === 'string' ? opcoes.identityToken : null;
    this.identityCallback = typeof opcoes.getIdentityToken === 'function' ? opcoes.getIdentityToken as IdentityCallback : null;
    this.contextCallback = typeof opcoes.getContextToken === 'function' ? opcoes.getContextToken as IdentityCallback : null;
    this.whenBody(() => this.mount());
  }

  /**
   * O usuário acabou de entrar no software hospedeiro: `NuklieChat('identify', jwt)`.
   *
   * Com o iframe pronto, o token vai agora; sem ele, espera na memória até o `init` do iframe. Um
   * `identify` novo substitui o que ainda não foi.
   */
  identify(token: unknown): void {
    if (!this.options) {
      this.configError('Chame init antes de identify.');

      return;
    }

    if (!looksLikeJwt(token)) {
      this.configError('identify precisa do JWT gerado pelo seu backend.');

      return;
    }

    if (this.iframe && this.frameReady) {
      this.post('identity.set', { token });
      this.pendingIdentity = null;

      return;
    }

    this.pendingIdentity = token;
  }

  open(): void {
    if (!this.options) return;

    if (!this.panel || !this.launcherReady) {
      this.pendingOpen = true;

      return;
    }

    if (this.isOpen) return;

    this.isOpen = true;
    // Alguém pediu o chat: este iframe deixou de ser só um avaliador de regras.
    this.preloadedForProactive = false;
    // O balão some ao abrir: ele existe para trazer a pessoa até aqui, e cumpriu o papel.
    this.clearTeaser();
    this.ensureIframe();
    this.panel.setAttribute('data-open', 'true');
    this.launcher?.setAttribute('aria-expanded', 'true');
    this.launcher?.setAttribute('aria-label', 'Fechar chat');
    if (this.launcher) this.launcher.innerHTML = LAUNCHER_ICON_CLOSE;
    this.post('open', {});
    this.iframe?.focus();
    this.emit('open', {});
  }

  close(): void {
    if (!this.isOpen || !this.panel) return;

    this.isOpen = false;
    this.panel.setAttribute('data-open', 'false');
    this.launcher?.setAttribute('aria-expanded', 'false');
    this.launcher?.setAttribute('aria-label', 'Abrir chat');
    if (this.launcher) this.launcher.innerHTML = LAUNCHER_ICON_OPEN;
    this.post('close', {});
    this.launcher?.focus();
    this.emit('close', {});
  }

  /**
   * O retrato da tela atual: `NuklieChat('setContext', { route, display, entities, metadata })`.
   *
   * Declarado pela página — o atendente e o agente veem como "informado pelo navegador", e nada disso
   * autoriza coisa alguma. Substitui o anterior.
   */
  setContext(raw: unknown): void {
    if (!this.options) {
      this.configError('Chame init antes de setContext.');

      return;
    }

    const preparado = prepareClaimedContext(raw);

    if ('error' in preparado) {
      this.configError(preparado.error);

      return;
    }

    if (this.iframe && this.frameReady) {
      this.post('context.claimed.set', { context: preparado.context });
      this.pendingClaimedContext = null;

      return;
    }

    this.pendingClaimedContext = preparado.context;
  }

  /** `NuklieChat('clearContext')`: a conversa atual fica sem contexto do hospedeiro. */
  clearContext(): void {
    if (!this.options) return;

    this.pendingClaimedContext = null;
    this.pendingContextToken = null;

    if (this.iframe && this.frameReady) this.post('context.claimed.clear', {});
  }

  /** `NuklieChat('setVerifiedContext', token)`: contexto assinado pelo backend do hospedeiro. */
  setVerifiedContext(token: unknown): void {
    if (!this.options) {
      this.configError('Chame init antes de setVerifiedContext.');

      return;
    }

    if (!looksLikeJwt(token, CONTEXT_TOKEN_MAX)) {
      this.configError('setVerifiedContext precisa do token de contexto gerado pelo seu backend.');

      return;
    }

    if (this.iframe && this.frameReady) {
      this.post('context.verified.set', { token });
      this.pendingContextToken = null;

      return;
    }

    this.pendingContextToken = token;
  }

  /**
   * Logout no hospedeiro: este navegador para de representar o visitante.
   *
   * Some o token de identidade que esperava, e some o `getIdentityToken` — ele buscaria o token de quem
   * acabou de sair. Para identificar outro usuário, `identify` (ou `destroy` + `init`). O visitante
   * continua existindo no servidor, com o histórico: é só este navegador que o esquece.
   */
  reset(): void {
    if (!this.options) return;

    this.pendingIdentity = null;
    this.identityCallback = null;
    this.identityInFlight = null;
    // O contexto era da tela de quem saiu.
    this.pendingClaimedContext = null;
    this.pendingContextToken = null;
    this.contextCallback = null;
    this.contextInFlight = null;

    // Sem iframe ainda, o pedido fica guardado e vai no `init` do iframe — antes de qualquer sessão.
    if (this.iframe && this.frameReady) {
      this.post('identity.clear', {});
    } else {
      this.resetPending = true;
    }

    this.setUnread(0);
  }

  destroy(): void {
    this.win.removeEventListener('message', this.onMessage);
    this.win.removeEventListener('resize', this.onResize);
    this.win.removeEventListener('keydown', this.onKeydown);
    this.doc.removeEventListener('visibilitychange', this.onVisibility);
    this.configScript?.remove();
    delete this.win.__nuklieChatLauncher;
    this.clearProactiveTimers();
    this.win.removeEventListener('popstate', this.onNavigation);
    (this.win as WindowWithNavigation).navigation?.removeEventListener('navigate', this.onNavigation);
    this.host?.remove();

    this.options = null;
    this.host = this.root = this.launcher = this.badge = this.panel = this.iframe = this.configScript = null;
    this.frameReady = this.isOpen = this.pendingOpen = this.resetPending = this.launcherReady = false;
    this.pendingIdentity = null;
    this.identityCallback = null;
    this.identityInFlight = null;
    this.pendingClaimedContext = null;
    this.pendingContextToken = null;
    this.contextCallback = null;
    this.contextInFlight = null;
    this.teaser = null;
    this.pendingEvents = [];
    this.lastPath = null;
    this.preloadedForProactive = false;
    // Os ouvintes do integrador saem junto: um `init` depois de `destroy` começa do zero.
    this.listeners.clear();
  }

  on(evento: WebchatEventName, callback: Callback): void {
    if (!EVENTS.includes(evento as WebchatEventName) || typeof callback !== 'function') {
      this.configError(`on precisa de um evento conhecido (${EVENTS.join(', ')}) e de uma função.`);

      return;
    }

    const lista = this.listeners.get(evento as WebchatEventName) ?? new Set<Callback>();
    lista.add(callback as Callback);
    this.listeners.set(evento as WebchatEventName, lista);
  }

  off(evento: WebchatEventName, callback: Callback): void {
    this.listeners.get(evento as WebchatEventName)?.delete(callback as Callback);
  }

  /** Estado para testes e depuração — nada sensível. */
  state() {
    return { initialized: this.options !== null, open: this.isOpen, iframe: this.iframe !== null, unread: this.unread, frameReady: this.frameReady };
  }

  private mount(): void {
    if (!this.options || this.host) return;

    const host = this.doc.createElement('nuklie-webchat');
    host.id = 'nuklie-webchat-root';
    // Inline e `!important`: o elemento anfitrião ainda está no DOM da página, e um `* { display: none }`
    // do site o esconderia.
    host.style.setProperty('all', 'initial', 'important');
    host.style.setProperty('display', 'block', 'important');
    host.style.setProperty('position', 'static', 'important');

    const root = host.attachShadow({ mode: 'closed' });
    const estilo = this.doc.createElement('style');
    estilo.textContent = LAUNCHER_CSS.replace('__Z__', String(Z_INDEX));
    root.appendChild(estilo);

    const panel = this.doc.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('data-open', 'false');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Chat');

    const launcher = this.doc.createElement('button');
    launcher.type = 'button';
    launcher.className = 'launcher';
    launcher.setAttribute('aria-label', 'Abrir chat');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.innerHTML = LAUNCHER_ICON_OPEN;
    launcher.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));

    const badge = this.doc.createElement('span');
    badge.className = 'badge';
    badge.setAttribute('aria-live', 'polite');
    badge.hidden = true;

    const wrapper = this.doc.createElement('div');
    wrapper.className = 'wrap';
    wrapper.setAttribute('data-position', 'bottom_right');
    wrapper.setAttribute('data-ready', 'false');
    wrapper.append(panel, launcher, badge);
    root.appendChild(wrapper);

    this.host = host;
    this.root = root;
    this.panel = panel;
    this.launcher = launcher;
    this.badge = badge;
    this.doc.body.appendChild(host);

    this.win.addEventListener('message', this.onMessage);
    this.win.addEventListener('resize', this.onResize);
    this.win.addEventListener('keydown', this.onKeydown);
    this.doc.addEventListener('visibilitychange', this.onVisibility);

    this.loadLauncherConfig();
  }

  /** Cor e posição, por `<script>` da origem do chat. Ver `WebchatPublicController::launcher`. */
  private loadLauncherConfig(): void {
    if (!this.options) return;

    const chave = this.options.publicKey;

    this.win.__nuklieChatLauncher = (config: LauncherConfig) => {
      if (!this.options || config?.public_key !== chave) return;
      delete this.win.__nuklieChatLauncher;
      this.applyLauncherConfig(config);
    };

    const script = this.doc.createElement('script');
    script.async = true;
    script.src = `${this.frameOrigin}/api/v1/public/webchat/launcher/show?public_key=${encodeURIComponent(chave)}`;
    script.onerror = () => {
      this.emit('error', { code: 'unavailable' });
      this.host?.remove();
    };
    this.configScript = script;
    this.doc.head.appendChild(script);
  }

  applyLauncherConfig(config: LauncherConfig): void {
    const wrap = this.root?.querySelector('.wrap') as HTMLElement | null;

    if (!wrap) return;

    const cor = config.primary_color && /^#[0-9a-fA-F]{6}$/.test(config.primary_color) ? config.primary_color : '#4f46e5';
    wrap.style.setProperty('--nk-color', cor);
    wrap.setAttribute('data-position', config.position === 'bottom_left' ? 'bottom_left' : 'bottom_right');
    wrap.setAttribute('data-ready', 'true');
    this.launcherReady = true;
    this.configScript?.remove();
    this.configScript = null;

    this.emit('ready', {});

    if (this.pendingOpen) {
      this.pendingOpen = false;
      this.open();

      return;
    }

    /*
     * Só quando este chat tem regra proativa.
     *
     * Sem isto, ou todo site paga um iframe oculto que nunca será usado, ou nenhuma regra dispara antes
     * do primeiro clique — que é justamente quando ela precisa disparar.
     */
    if (config.proactive === true) {
      this.watchNavigation();
      this.preloadForProactive();
    }
  }

  /**
   * `NuklieChat("track", "checkout.started", { code: "…" })`.
   *
   * ## O payload é DECLARADO
   *
   * Ele vem do JavaScript da página, que qualquer pessoa com o console aberto controla. Serve para
   * decidir se um balão aparece — nada além. Não identifica ninguém, não autoriza nada e não vira
   * entrada de ferramenta. É a mesma regra do contexto declarado da Fase 8.
   *
   * ## Nem sai daqui quando não há para quem mandar
   *
   * Sem iframe preparado o evento fica guardado e é entregue quando ele responder. Sem regra proativa
   * nenhuma o iframe nunca é preparado antes do primeiro clique, e o evento simplesmente se perde — é o
   * certo: isto não é analytics de produto, e não existe para registrar nada.
   */
  track(nome: unknown, payload: unknown): void {
    const preparado = prepareTrackEvent(nome, payload);

    if ('error' in preparado) {
      this.configError(preparado.error);

      return;
    }

    if (this.frameReady) {
      this.post('host.track', preparado);

      return;
    }

    // Fila curta: um site que dispara cem eventos antes de o iframe existir não vale cem mensagens.
    if (this.pendingEvents.length < 20) this.pendingEvents.push(preparado);
  }

  /**
   * Conta ao iframe em que página estamos.
   *
   * Só o caminho e o título — nunca a URL com query, que arrasta identificador de campanha e às vezes
   * token que alguém pôs na barra de endereço.
   */
  private reportRoute(): void {
    const path = this.win.location?.pathname ?? '/';

    if (path === this.lastPath && this.frameReady) return;

    this.lastPath = path;

    if (this.frameReady) this.post('host.route', { path, title: this.doc.title || null });
  }

  /**
   * Observa a navegação SEM tocar no `history` do site.
   *
   * A tentação é substituir `pushState` para perceber troca de tela em SPA. É o que muitos widgets
   * fazem, e é mexer num global da página de outra pessoa: dois scripts fazendo isso já brigam, e o
   * bug aparece no roteador do cliente, não no nosso.
   *
   * `popstate` cobre voltar e avançar; a Navigation API cobre SPA nos navegadores que a têm. Quem usa
   * SPA em navegador antigo continua descrevendo a tela por `setContext({ route })`, que é o caminho da
   * Fase 8 e já existia antes desta.
   */
  private watchNavigation(): void {
    this.win.addEventListener('popstate', this.onNavigation);
    (this.win as WindowWithNavigation).navigation?.addEventListener('navigate', this.onNavigation);
  }

  /**
   * Prepara o iframe quando há regra proativa, sem atrapalhar a página.
   *
   * `requestIdleCallback` para não disputar com o carregamento do site — o widget é convidado na página
   * de outra pessoa. Sem a API, um atraso fixo generoso faz o mesmo papel.
   *
   * O iframe criado aqui NÃO abre sessão: ele recebe `init` com `proactive` e só busca configuração e
   * regras. Ver `useWebchat`.
   */
  private preloadForProactive(): void {
    const preparar = () => {
      this.idleHandle = null;
      this.preloadTimer = null;
      if (this.iframe || !this.options) return;
      this.preloadedForProactive = true;
      this.ensureIframe();
    };

    const idle = (this.win as WindowWithIdle).requestIdleCallback;

    if (typeof idle === 'function') {
      this.idleHandle = idle.call(this.win, preparar, { timeout: 4000 });

      return;
    }

    this.preloadTimer = setTimeout(preparar, 2500);
  }

  private clearProactiveTimers(): void {
    if (this.preloadTimer !== null) {
      clearTimeout(this.preloadTimer);
      this.preloadTimer = null;
    }

    const cancel = (this.win as WindowWithIdle).cancelIdleCallback;

    if (this.idleHandle !== null && typeof cancel === 'function') {
      cancel.call(this.win, this.idleHandle);
      this.idleHandle = null;
    }
  }

  /**
   * Desenha o que a regra pediu.
   *
   * O balão é do CARREGADOR, e não do iframe: enquanto ninguém abriu o chat o iframe tem tamanho zero e
   * fica atrás do botão, sem onde aparecer.
   *
   * O texto entra por `textContent`. Ele é escrito por quem administra o chat e renderizado no site de
   * outra pessoa; aceitar HTML aqui daria a quem configura — e a quem invadisse aquela conta — um
   * `innerHTML` na página do cliente, entregue pelo nosso próprio script.
   */
  private applyProactive(payload: FrameToHost['proactive.show']): void {
    if (!this.root || this.isOpen) return;

    if (payload.action === 'open_chat') {
      this.emit('proactive_shown', { rule_id: payload.rule_id });
      this.open();

      return;
    }

    if (payload.action === 'highlight_launcher') {
      /*
       * Discreto, e desligado para quem pediu menos movimento.
       *
       * `prefers-reduced-motion` não é preferência estética: quem o liga costuma ter motivo vestibular
       * ou de atenção, e uma animação insistente no canto da tela é exatamente o que incomoda.
       */
      this.launcher?.setAttribute('data-highlight', 'true');
      this.emit('proactive_shown', { rule_id: payload.rule_id });

      return;
    }

    this.clearTeaser();

    const balao = this.doc.createElement('div');
    balao.className = 'teaser';
    balao.setAttribute('role', 'status');

    const texto = this.doc.createElement('button');
    texto.type = 'button';
    texto.className = 'teaser-text';
    texto.textContent = payload.text;
    texto.addEventListener('click', () => {
      this.post('proactive.clicked', {});
      this.emit('proactive_clicked', { rule_id: payload.rule_id });
      this.clearTeaser();
      this.open();
    });

    const fechar = this.doc.createElement('button');
    fechar.type = 'button';
    fechar.className = 'teaser-close';
    fechar.setAttribute('aria-label', 'Fechar');
    fechar.textContent = '×';
    fechar.addEventListener('click', () => {
      this.post('proactive.dismissed', {});
      this.emit('proactive_dismissed', { rule_id: payload.rule_id });
      this.clearTeaser();
    });

    balao.appendChild(texto);
    balao.appendChild(fechar);
    this.root.querySelector('.wrap')?.appendChild(balao);
    this.teaser = balao;

    this.emit('proactive_shown', { rule_id: payload.rule_id });
  }

  private clearTeaser(): void {
    this.teaser?.remove();
    this.teaser = null;
    this.launcher?.removeAttribute('data-highlight');
  }

  private ensureIframe(): void {
    if (this.iframe || !this.panel || !this.options) return;

    const iframe = this.doc.createElement('iframe');
    const origem = this.win.location.origin;
    iframe.src = `${this.frameOrigin}/webchat/frame?public_key=${encodeURIComponent(this.options.publicKey)}&origin=${encodeURIComponent(origem)}`;
    iframe.title = 'Chat';
    // O necessário: rodar o app, guardar a sessão na origem dele, enviar formulário e abrir anexos/links.
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
    iframe.setAttribute('referrerpolicy', 'strict-origin');
    iframe.setAttribute('allow', 'clipboard-write');
    this.iframe = iframe;
    this.panel.appendChild(iframe);
  }

  private handleFrameMessage(event: MessageEvent): void {
    const msg = readEnvelope<FrameToHost>(event, this.iframe?.contentWindow, this.frameOrigin, FRAME_TO_HOST_TYPES);

    if (!msg) return;

    switch (msg.type) {
      case 'ready':
        this.frameReady = true;
        this.post('init', {
          locale: this.options?.locale ?? null,
          open: this.isOpen,
          resetSession: this.resetPending,
          identity: this.identitySource(),
          contextCallback: this.contextCallback !== null,
          /*
           * A marca que impede a sessão.
           *
           * Sem ela o app trataria este `init` como qualquer outro e abriria sessão — e o iframe
           * preparado em toda página criaria um visitante por página vista.
           */
          proactive: this.preloadedForProactive,
        });
        this.resetPending = false;
        if (this.pendingIdentity) {
          this.post('identity.set', { token: this.pendingIdentity });
          this.pendingIdentity = null;
        }
        // Declarado antes do verificado: se os dois esperavam, o verificado é o que fica.
        if (this.pendingClaimedContext) {
          this.post('context.claimed.set', { context: this.pendingClaimedContext });
          this.pendingClaimedContext = null;
        }
        if (this.pendingContextToken) {
          this.post('context.verified.set', { token: this.pendingContextToken });
          this.pendingContextToken = null;
        }
        this.post('host.viewport', { mobile: this.isMobile() });
        /*
         * A página em que a pessoa já está conta como visita.
         *
         * Ela não vai navegar de novo só para nós, e uma regra de `/financeiro` precisa valer para quem
         * ABRIU o site no financeiro — que é a maioria.
         */
        this.lastPath = null;
        this.reportRoute();
        for (const evento of this.pendingEvents.splice(0)) this.post('host.track', evento);
        break;
      case 'context.request':
        void this.answerContextRequest(String((msg.payload as FrameToHost['context.request']).request_id ?? ''));
        break;
      case 'identity.request':
        void this.answerIdentityRequest(String((msg.payload as FrameToHost['identity.request']).request_id ?? ''));
        break;
      case 'proactive.show':
        this.applyProactive(msg.payload as FrameToHost['proactive.show']);
        break;
      case 'proactive.clear':
        this.clearTeaser();
        break;
      case 'identity_changed':
        this.emit('identity_changed', { authenticated: (msg.payload as FrameToHost['identity_changed']).authenticated === true });
        break;
      case 'unread_count': {
        const count = Number((msg.payload as FrameToHost['unread_count']).count);
        this.setUnread(Number.isFinite(count) && count > 0 ? Math.min(Math.floor(count), 999) : 0);
        break;
      }
      case 'conversation_started':
        this.emit('conversation_started', {});
        break;
      case 'conversation_closed':
        this.emit('conversation_closed', {});
        break;
      case 'message_received': {
        const p = msg.payload as FrameToHost['message_received'];
        this.emit('message_received', { id: String(p.id ?? ''), direction: 'inbound', sent_at: typeof p.sent_at === 'string' ? p.sent_at : null });
        break;
      }
      case 'error':
        this.emit('error', { code: String((msg.payload as FrameToHost['error']).code ?? 'unknown') });
        break;
      case 'request_open':
        this.open();
        break;
      case 'request_close':
        this.close();
        break;
      default:
        break;
    }
  }

  private identitySource(): IdentitySource {
    if (this.pendingIdentity) return 'token';

    return this.identityCallback ? 'callback' : 'none';
  }

  /**
   * O iframe pediu identidade: chama o `getIdentityToken` do hospedeiro (uma vez por vez, com prazo) e
   * responde. A função roda aqui, na página — o iframe nunca a vê, e a página nunca vê o que o iframe
   * faz com o token.
   */
  private async answerIdentityRequest(requestId: string): Promise<void> {
    if (requestId === '') return;

    const callback = this.identityCallback;

    if (!callback) {
      this.post('identity.response', { request_id: requestId, error: 'unavailable' });

      return;
    }

    try {
      const token = await this.fetchIdentity(callback);

      // Um `reset` no meio do caminho: o token é de quem saiu.
      if (this.identityCallback !== callback) {
        this.post('identity.response', { request_id: requestId, error: 'unavailable' });

        return;
      }

      this.post('identity.response', { request_id: requestId, token });
    } catch {
      this.post('identity.response', { request_id: requestId, error: 'failed' });
    }
  }

  /** Mesmo desenho de `answerIdentityRequest`, para `getContextToken`. */
  private async answerContextRequest(requestId: string): Promise<void> {
    if (requestId === '') return;

    const callback = this.contextCallback;

    if (!callback) {
      this.post('context.response', { request_id: requestId, error: 'unavailable' });

      return;
    }

    try {
      if (!this.contextInFlight) {
        const pedido = this.callWithDeadline(callback, CONTEXT_TOKEN_MAX, 'getContextToken');
        this.contextInFlight = pedido;
        const limpar = () => { if (this.contextInFlight === pedido) this.contextInFlight = null; };
        pedido.then(limpar, limpar);
      }

      const token = await this.contextInFlight;

      if (this.contextCallback !== callback) {
        this.post('context.response', { request_id: requestId, error: 'unavailable' });

        return;
      }

      this.post('context.response', { request_id: requestId, token });
    } catch {
      this.post('context.response', { request_id: requestId, error: 'failed' });
    }
  }

  private callWithDeadline(callback: IdentityCallback, max: number, nome: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const prazo = this.win.setTimeout(() => reject(new Error('timeout')), IDENTITY_CALLBACK_TIMEOUT_MS);

      Promise.resolve()
        .then(() => callback())
        .then(token => {
          if (!looksLikeJwt(token, max)) throw new Error('invalid');
          resolve(token);
        })
        .catch(erro => {
          console.error(`[NuklieChat] ${nome} falhou ou não devolveu um token.`);
          reject(erro);
        })
        .finally(() => this.win.clearTimeout(prazo));
    });
  }

  private fetchIdentity(callback: IdentityCallback): Promise<string> {
    if (this.identityInFlight) return this.identityInFlight;

    const pedido = new Promise<string>((resolve, reject) => {
      const prazo = this.win.setTimeout(() => reject(new Error('timeout')), IDENTITY_CALLBACK_TIMEOUT_MS);

      Promise.resolve()
        .then(() => callback())
        .then(token => {
          if (!looksLikeJwt(token)) throw new Error('invalid');
          resolve(token);
        })
        .catch(erro => {
          // Nunca o erro inteiro: pode carregar a resposta do backend do hospedeiro.
          console.error('[NuklieChat] getIdentityToken falhou ou não devolveu um JWT.');
          reject(erro);
        })
        .finally(() => this.win.clearTimeout(prazo));
    });

    this.identityInFlight = pedido;
    const limpar = () => {
      if (this.identityInFlight === pedido) this.identityInFlight = null;
    };
    pedido.then(limpar, limpar);

    return pedido;
  }

  private setUnread(count: number): void {
    if (count === this.unread) return;

    this.unread = count;

    if (this.badge) {
      this.badge.hidden = count === 0;
      this.badge.textContent = count > 99 ? '99+' : String(count);
      this.badge.setAttribute('aria-label', count === 1 ? '1 mensagem não lida' : `${count} mensagens não lidas`);
    }

    this.emit('unread_count_changed', { count });
  }

  private post<K extends keyof HostToFrame>(type: K, payload: HostToFrame[K]): void {
    if (!this.iframe?.contentWindow || !this.frameReady) return;

    this.iframe.contentWindow.postMessage(envelope<HostToFrame, K>(type, payload), this.frameOrigin);
  }

  private emit(evento: WebchatEventName, payload: Record<string, unknown>): void {
    for (const callback of this.listeners.get(evento) ?? []) {
      try {
        callback({ ...payload });
      } catch (erro) {
        console.error('[NuklieChat] Erro num ouvinte de', evento, erro);
      }
    }
  }

  private configError(mensagem: string): void {
    console.error(`[NuklieChat] ${mensagem}`);
    this.emit('error', { code: 'invalid_config', message: mensagem });
  }

  private isMobile(): boolean {
    return this.win.innerWidth <= 480;
  }

  private whenBody(fn: () => void): void {
    if (this.doc.body) {
      fn();

      return;
    }

    this.doc.addEventListener('DOMContentLoaded', fn, { once: true });
  }
}

export interface NuklieChatGlobal {
  (command: string, ...args: unknown[]): void;
  q?: ArrayLike<unknown>[];
  version?: string;
}

/**
 * Troca o stub da página pelo carregador de verdade e reproduz a fila, na ordem.
 */
export function installGlobal(win: Window & { NuklieChat?: NuklieChatGlobal }, frameOrigin: string, version: string): NuklieChatLoader {
  const fila: ArrayLike<unknown>[] = Array.from(win.NuklieChat?.q ?? []);
  const loader = new NuklieChatLoader(win, frameOrigin);

  const api: NuklieChatGlobal = (command: string, ...args: unknown[]) => loader.command(command, ...args);
  api.version = version;
  win.NuklieChat = api;

  for (const chamada of fila) {
    const [command, ...args] = Array.from(chamada);
    loader.command(command, ...args);
  }

  return loader;
}
